import React, { useEffect, useMemo, useRef, useState } from 'react';
import useFirestore from '../hooks/useFirestore';
import useCoinGecko from '../hooks/useCoinGecko';
import { handleSaveWatchlistIds } from '../firebase/actions';
import {
  CoinGeckoMarket,
  CoinGeckoResourceState,
  CoinGeckoSearchResult,
} from '../types';
import { buildSearchResourceKey } from '../utils/coingecko';

function renderSparkline(prices: number[], color: string) {
  if (!prices || prices.length < 2) return null;
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const range = max - min || 1;
  const width = 80;
  const height = 36;
  const step = width / (prices.length - 1);
  const path = prices.map((price, index) => {
    const x = index * step;
    const y = height - ((price - min) / range) * height;
    return `${index === 0 ? 'M' : 'L'} ${x},${y}`;
  }).join(' ');

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} fill="none" className="overflow-visible">
      <path d={path} stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function formatPrice(price: number): string {
  if (price < 0.0001) return `$${price.toFixed(8)}`;
  if (price < 0.01) return `$${price.toFixed(6)}`;
  if (price < 1) return `$${price.toFixed(4)}`;
  return `$${price.toLocaleString()}`;
}

function formatMarketCap(value: number): string {
  if (value >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
  if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
  return `$${value.toLocaleString()}`;
}

const WatchlistPage: React.FC = () => {
  const watchlistIdsFromFirestore = useFirestore<string[]>('crypto-missions:watchlistIds', []);
  const { ensureMarkets, searchCoins, getMarketById, resources } = useCoinGecko();
  const [watchlistIds, setWatchlistIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialSyncDone = useRef(false);

  useEffect(() => {
    if (!initialSyncDone.current && watchlistIdsFromFirestore.length === 0) return;
    initialSyncDone.current = true;
    setWatchlistIds(watchlistIdsFromFirestore);
  }, [watchlistIdsFromFirestore]);

  useEffect(() => {
    if (watchlistIds.length === 0) {
      return;
    }
    void ensureMarkets(watchlistIds);
  }, [ensureMarkets, watchlistIds]);

  const searchKey = buildSearchResourceKey(searchQuery);
  const searchState = resources[searchKey] as CoinGeckoResourceState<CoinGeckoSearchResult[]> | undefined;
  const searchResults = searchState?.data ?? [];
  const marketsState = resources.markets as CoinGeckoResourceState<string[]> | undefined;

  const orderedCoins = useMemo(() => {
    return watchlistIds
      .map((id) => getMarketById(id))
      .filter(Boolean) as CoinGeckoMarket[];
  }, [getMarketById, watchlistIds]);

  const pendingIds = watchlistIds.filter((id) => !getMarketById(id));
  const isFetching = marketsState?.status === 'loading' || pendingIds.length > 0;
  const fetchError = marketsState?.status === 'error' && orderedCoins.length === 0;

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    if (!query.trim()) {
      return;
    }
    searchDebounceRef.current = setTimeout(() => {
      void searchCoins(query);
    }, 400);
  };

  const addToWatchlist = (coinId: string) => {
    if (watchlistIds.includes(coinId)) return;
    const newIds = [...watchlistIds, coinId];
    initialSyncDone.current = true;
    setWatchlistIds(newIds);
    void ensureMarkets(newIds);
    handleSaveWatchlistIds(newIds);
    setSearchQuery('');
    setShowSearch(false);
  };

  const removeFromWatchlist = (coinId: string) => {
    const newIds = watchlistIds.filter((id) => id !== coinId);
    setWatchlistIds(newIds);
    handleSaveWatchlistIds(newIds);
  };

  return (
    <div className="mx-auto w-full max-w-4xl px-4 py-6 md:px-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-fuchsia-800/80">Watchlist</h2>
          <p className="mt-1 text-sm text-gray-500">
            Tracking {watchlistIds.length} coin{watchlistIds.length === 1 ? '' : 's'}
            {isFetching && <span className="ml-2 animate-pulse text-fuchsia-400">Updating...</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {fetchError && (
            <button
              onClick={() => { void ensureMarkets(watchlistIds, { force: true }); }}
              className="rounded-lg bg-orange-100 px-3 py-1.5 text-xs font-bold text-orange-600 transition-colors hover:bg-orange-200"
            >
              Retry
            </button>
          )}
          <button
            onClick={() => setShowSearch((value) => !value)}
            className="flex items-center gap-2 rounded-xl bg-fuchsia-500 px-4 py-2 text-sm font-bold text-white shadow-md transition-colors hover:bg-fuchsia-600"
          >
            <span className="text-lg leading-none">+</span>
            Add Coin
          </button>
        </div>
      </div>

      {showSearch && (
        <div className="mb-4 rounded-2xl border border-white/40 bg-white/60 p-4 shadow-xl backdrop-blur-lg">
          <div className="mb-2 flex items-center gap-2">
            <div className="relative flex-1">
              <input
                autoFocus
                type="text"
                value={searchQuery}
                onChange={(event) => handleSearch(event.target.value)}
                onBlur={() => setTimeout(() => { setShowSearch(false); setSearchQuery(''); }, 200)}
                placeholder="Search by coin name or symbol"
                className="w-full rounded-xl border border-fuchsia-200 bg-white/80 px-4 py-2.5 text-sm focus:border-fuchsia-400 focus:outline-none"
              />
              {searchState?.status === 'loading' && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">Searching...</span>
              )}
            </div>
            <button
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => { setShowSearch(false); setSearchQuery(''); }}
              className="rounded-xl bg-gray-100 px-3 py-2.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-200"
            >
              Cancel
            </button>
          </div>
          {searchResults.length > 0 && (
            <div className="overflow-hidden rounded-xl border border-fuchsia-100 bg-white shadow-lg">
              {searchResults.map((coin) => (
                <button
                  key={coin.id}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => addToWatchlist(coin.id)}
                  disabled={watchlistIds.includes(coin.id)}
                  className={`flex w-full items-center gap-3 border-b border-gray-50 px-4 py-3 text-left transition-colors last:border-0 ${
                    watchlistIds.includes(coin.id) ? 'cursor-not-allowed opacity-40' : 'hover:bg-fuchsia-50'
                  }`}
                >
                  <img src={coin.thumb} alt={coin.name} className="h-7 w-7 rounded-full" />
                  <div className="flex-1">
                    <span className="text-sm font-bold text-gray-800">{coin.name}</span>
                    <span className="ml-2 text-xs uppercase text-gray-400">{coin.symbol}</span>
                  </div>
                  <span className="text-xs font-bold text-fuchsia-500">
                    {watchlistIds.includes(coin.id) ? 'Added' : '+ Add'}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {fetchError && orderedCoins.length === 0 && (
        <div className="mb-4 flex items-center justify-between rounded-xl border border-orange-200 bg-orange-50 p-4 text-sm text-orange-700">
          <span>CoinGecko is temporarily unavailable for this watchlist request.</span>
          <button onClick={() => { void ensureMarkets(watchlistIds, { force: true }); }} className="ml-3 font-bold underline">Retry</button>
        </div>
      )}

      {watchlistIds.length === 0 ? (
        <div className="py-20 text-center">
          <div className="mb-4 text-5xl">☆</div>
          <p className="mb-2 font-medium text-gray-500">Add your first coin to start tracking it.</p>
          <p className="text-sm text-gray-400">Use the Add Coin button above.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-white/20 bg-white/40 shadow-lg backdrop-blur-lg">
          <div className="grid grid-cols-12 border-b border-white/40 bg-white/30 px-5 py-3">
            <div className="col-span-4 text-xs font-bold uppercase tracking-wider text-gray-500">Coin</div>
            <div className="col-span-2 text-right text-xs font-bold uppercase tracking-wider text-gray-500">Price</div>
            <div className="col-span-2 text-right text-xs font-bold uppercase tracking-wider text-gray-500">24h</div>
            <div className="col-span-2 hidden text-right text-xs font-bold uppercase tracking-wider text-gray-500 md:block">Market Cap</div>
            <div className="col-span-2 text-right text-xs font-bold uppercase tracking-wider text-gray-500">7d</div>
          </div>

          {orderedCoins.map((coin) => {
            const isPositive = coin.priceChangePercentage24h >= 0;
            const color = isPositive ? '#10B981' : '#EF4444';
            return (
              <div key={coin.id} className="group grid grid-cols-12 items-center border-b border-white/20 px-5 py-4 transition-colors hover:bg-white/30">
                <div className="col-span-4 flex items-center gap-3">
                  <img src={coin.image} alt={coin.name} className="h-9 w-9 rounded-full shadow-sm" />
                  <div>
                    <div className="text-sm font-bold text-gray-800">{coin.name}</div>
                    <div className="text-xs uppercase text-gray-400">{coin.symbol}</div>
                  </div>
                </div>
                <div className="col-span-2 text-right">
                  <div className="font-mono text-sm font-bold text-gray-800">{formatPrice(coin.currentPrice)}</div>
                </div>
                <div className="col-span-2 text-right">
                  <span className={`text-sm font-bold ${isPositive ? 'text-emerald-600' : 'text-red-500'}`}>
                    {isPositive ? '+' : ''}{coin.priceChangePercentage24h.toFixed(2)}%
                  </span>
                </div>
                <div className="col-span-2 hidden text-right md:block">
                  <span className="text-xs text-gray-500">{formatMarketCap(coin.marketCap)}</span>
                </div>
                <div className="col-span-2 flex items-center justify-end gap-2">
                  {renderSparkline(coin.sparklineIn7d, color)}
                  <button
                    onClick={() => removeFromWatchlist(coin.id)}
                    className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-red-100 text-sm font-bold text-red-500 opacity-0 transition-opacity hover:bg-red-200 group-hover:opacity-100"
                    title="Remove"
                  >
                    ×
                  </button>
                </div>
              </div>
            );
          })}

          {pendingIds.map((id) => (
            <div key={id} className="grid grid-cols-12 items-center border-b border-white/20 px-5 py-4">
              <div className="col-span-4 flex items-center gap-3">
                <div className="h-9 w-9 animate-pulse rounded-full bg-gray-200" />
                <div>
                  <div className="mb-1 h-3 w-20 animate-pulse rounded bg-gray-200" />
                  <div className="h-2 w-10 animate-pulse rounded bg-gray-100" />
                </div>
              </div>
              <div className="col-span-2 flex justify-end">
                <div className="h-3 w-16 animate-pulse rounded bg-gray-200" />
              </div>
              <div className="col-span-2 flex justify-end">
                <div className="h-3 w-10 animate-pulse rounded bg-gray-200" />
              </div>
              <div className="col-span-2 hidden justify-end md:flex">
                <div className="h-3 w-12 animate-pulse rounded bg-gray-200" />
              </div>
              <div className="col-span-2 flex justify-end">
                <div className="h-6 w-16 animate-pulse rounded bg-gray-100" />
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 text-center text-[11px] text-gray-400">
        Data by <a href="https://www.coingecko.com/" target="_blank" rel="noreferrer" className="underline hover:text-gray-600">CoinGecko</a>
      </div>
    </div>
  );
};

export default WatchlistPage;
